import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { Badge, Button } from "@chakra-ui/react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import { Tooltip, IconButton } from "@mui/material";
import { MdOutlinePersonAdd } from "react-icons/md";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import axios from "axios";

const rowPerPage = 10;

type ReceiverData = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  middle_name: string;
  gender: string;
  dob: string;
  dialcode: string;
  phone: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  status: string;
  kyc_status: string;
  created_at: string;
};

interface ReceiverSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (receiverData: ReceiverData) => void;
  currency: string;
}

interface PayloadType {
  [key: string]: any;
}

const ReceiverSelectorModal: React.FC<ReceiverSelectorModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  currency,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState<ReceiverData[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // Pagination state following receiver/index.tsx pattern
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(rowPerPage);
  const [rowCount, setRowCount] = useState<number>(rowPerPage);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      setSearchKeyword("");
      setTableData([]);
      setPage(0);
      setLastKeys({});
      setCurrentLastKey("");
      setRowCount(rowPerPage);
    }
  }, [isOpen]);

  const handleSearch = () => {
    if (!searchTerm) return;
    // Reset pagination when search
    setLastKeys({});
    setCurrentLastKey("");
    setPage(0);
    setSearchKeyword(searchTerm);
    fetchReceivers({
      page: 0,
      limit: pageSize,
      lastkey: "",
      keyword: searchTerm,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClear = () => {
    setSearchTerm("");
    setSearchKeyword("");
    setTableData([]);
    setPage(0);
    setLastKeys({});
    setCurrentLastKey("");
    setRowCount(rowPerPage);
  };

  const fetchReceivers = async (payload: PayloadType = {}) => {
    try {
      setIsLoading(true);

      const queryObject: PayloadType = {
        usertype: "m",
        asset: currency,
        create_payout: true,
        limit: payload?.limit || pageSize,
        lastkey: payload?.lastkey || "",
      };

      // Add search keyword with receiver_keyword field
      if (payload?.keyword && payload?.keyword !== "") {
        queryObject.receiver_keyword = payload.keyword;
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/receiver/func/get-all-receivers?${queryString}`
      );

      const newData = response.data.receivers?.payload || [];

      if (newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      // Manage Last Key and Total Record (following receiver/index.tsx pattern)
      const newLastKey = response.data.receivers?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        const newPage = payload?.page || 0;
        const newPageSize = payload?.limit || pageSize;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys((prev) => ({ ...prev, [newPage]: newLastKey }));
      } else {
        // No more pages
        const newPage = payload?.page || 0;
        const newPageSize = payload?.limit || pageSize;
        setRowCount(newPage * newPageSize + newData.length);
      }
    } catch (error) {
      console.error("Error fetching receivers", error);
      setTableData([]);
      // Clear all
      setPage(0);
      setRowCount(0);
      setLastKeys({});
      setCurrentLastKey("");
    } finally {
      setIsLoading(false);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newFilters: PayloadType = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      keyword: searchKeyword,
    };

    // If click prev pagination
    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        keyword: searchKeyword,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys({});
    } else {
      setPage(newValue.page);
    }

    fetchReceivers(newFilters);
  };

  const handleSelect = (receiver: ReceiverData) => {
    onSelect(receiver);
    onClose();
  };

  const columns: GridColDef[] = [
    {
      field: "first_name",
      headerName: "First Name",
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      flex: 1,
      minWidth: 120,
      sortable: false,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      flex: 1,
      minWidth: 100,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        return (
          <Tooltip title="Select Receiver" arrow placement="top">
            <IconButton
              id={"button-select-" + params?.row?.id}
              onClick={() => handleSelect(params.row)}
            >
              <MdOutlinePersonAdd className="h-5 w-5" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const customDataGridStyles: Partial<GridClasses> = {
    root: "mui-table-custom",
    columnHeader: "text-start",
    row: "",
  };

  return (
    <>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          closeOnOverlayClick={false}
          size="6xl"
        >
          <ModalOverlay className="bg-[#000] !opacity-30" />

          <ModalContent className="dark:!bg-neutral-900 !z-[9999] !m-auto !w-full !max-w-[70%] !rounded-2xl !border !border-gray-200 !bg-white !shadow-2xl dark:!border-white/10 md:!top-[8vh]">
            <ModalBody className="p-0">
              <div className="flex items-start justify-between gap-4 border-b border-gray-100 py-4 dark:border-white/10 md:px-7">
                <div className="flex items-start gap-3">
                  <div className="mt-1 rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-300 dark:ring-primary-900/30">
                    <svg
                      viewBox="0 0 24 24"
                      className="h-6 w-6"
                      fill="currentColor"
                    >
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </div>

                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Select Receiver
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Search by name, email, or reference and pick a receiver
                      for payout.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-3xl border border-gray-200 text-gray-600 transition hover:bg-gray-50 hover:text-gray-700 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
                  aria-label="Close"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 6 6 18" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="max-h-[70vh] overflow-y-auto">
              <div className="dark:bg-neutral-900 px-6 py-4 md:px-7">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="relative flex-1">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                      </svg>
                    </span>

                    <input
                      className="dark:bg-neutral-800 h-10 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 text-sm font-medium text-gray-900 shadow-sm outline-none transition focus:border-navy-400 focus:ring-2 focus:ring-navy-200 dark:border-white/10 dark:text-white dark:focus:ring-white/10"
                      type="text"
                      placeholder="Search receiver by name, email, reference..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={handleKeyDown}
                      autoFocus
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleClear}
                      isDisabled={(!searchTerm && !searchKeyword) || isLoading}
                      className="btn btn-default h-10 px-4"
                    >
                      Clear
                    </Button>

                    <Button
                      onClick={handleSearch}
                      isDisabled={!searchTerm || isLoading}
                      isLoading={isLoading}
                      loadingText="Searching..."
                      className="btn btn-primary h-10 px-4"
                    >
                      Search
                    </Button>
                  </div>
                </div>

                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Tip: Press{" "}
                  <span className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 dark:border-white/10 dark:bg-white/5">
                    Enter
                  </span>{" "}
                  to search.
                </div>
              </div>

              <div className="mb-4 px-6 pb-6 md:px-7">
                {!searchKeyword ? (
                  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 py-12 text-center dark:border-white/10 dark:bg-white/5">
                    <div className="dark:bg-neutral-900 mb-3 rounded-full bg-white p-3 text-gray-500 shadow-sm ring-1 ring-gray-100 dark:text-gray-300 dark:ring-white/10">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-6 w-6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                      Start searching receivers
                    </h3>
                    <p className="mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
                      Enter a keyword above and click Search to display matching
                      receivers.
                    </p>
                  </div>
                ) : (
                  <Box className="dark:bg-neutral-900 w-full rounded-xl border border-gray-200 bg-white dark:border-white/10">
                    <DataGrid
                      sx={{
                        "& .MuiDataGrid-cell": { padding: "8px 16px" },
                        "& .MuiDataGrid-columnHeader": { padding: "8px 16px" },
                        "& .MuiTablePagination-input": { marginRight: "-20px" },
                        "& .MuiTablePagination-displayedRows": {
                          display: "none",
                        },
                        border: "none",
                      }}
                      loading={isLoading}
                      rows={tableData || []}
                      columns={columns}
                      getRowId={(row) => row.id}
                      rowCount={rowCount}
                      sortingMode="server"
                      paginationMode="server"
                      rowHeight={48}
                      columnHeaderHeight={48}
                      paginationModel={{ page: page, pageSize: pageSize }}
                      onPaginationModelChange={setPaginationModel}
                      disableRowSelectionOnClick
                      disableColumnFilter
                      disableColumnMenu
                      classes={customDataGridStyles}
                      hideFooter={currentLastKey === "" && page === 0}
                      autoHeight
                      localeText={{ noRowsLabel: "No receivers found" }}
                    />
                  </Box>
                )}
              </div>
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ReceiverSelectorModal;
